import { useEffect, useState } from 'react';
import api from '../services/api';

const hasNotification = typeof Notification !== 'undefined';
const getNotifPermission = () => hasNotification ? Notification.permission : 'default';
const requestNotifPermission = () => hasNotification ? Notification.requestPermission() : Promise.resolve('denied');

export const usePushNotifications = () => {
  const [permission, setPermission] = useState(getNotifPermission());
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const urlBase64ToUint8Array = (base64String) => {
    const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
    const base64 = (base64String + padding)
      .replace(/\-/g, '+')
      .replace(/_/g, '/');
    const rawData = window.atob(base64);
    const outputArray = new Uint8Array(rawData.length);
    for (let i = 0; i < rawData.length; ++i) {
      outputArray[i] = rawData.charCodeAt(i);
    }
    return outputArray;
  };

  const subscribeUser = async () => {
    try {
      setLoading(true);
      setError(null);

      // 1. Registrar service worker
      const registration = await navigator.serviceWorker.ready;
      
      // 2. Obter subscription existente
      let subscription = await registration.pushManager.getSubscription();
      
      // 3. Se não existir, criar nova
      if (!subscription) {
        const vapidPublicKey = 'BOy3eoxYgTT0O-eWItfLqLqu0JtsJxBGo2oWlsSMHUJ4mHTPen-ja1uAMX9uWnegr4I2oqqRpGKeQKedB0hJcOM';
        const convertedKey = urlBase64ToUint8Array(vapidPublicKey);
        
        subscription = await registration.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: convertedKey
        });
      }

      // 4. Enviar subscription para o backend
      await api.post('/push/subscribe', subscription);
      
      setSubscription(subscription);
      return subscription;
    } catch (err) {
      setError(err.message);
      console.error('Erro ao subscrever:', err);
    } finally {
      setLoading(false);
    }
  };

  const unsubscribeUser = async () => {
    try {
      setLoading(true);
      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.getSubscription();
      
      if (subscription) {
        await subscription.unsubscribe();
        await api.post('/push/unsubscribe', { endpoint: subscription.endpoint });
        setSubscription(null);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const requestPermission = async () => {
    try {
      const result = await requestNotifPermission();
      setPermission(result);

      if (result === 'granted') {
        await subscribeUser();
      }
      return result;
    } catch (err) {
      setError(err.message);
    }
  };

  // Verificar subscription existente ao montar
  useEffect(() => {
    const checkSubscription = async () => {
      if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
        setError('Push notifications não suportadas');
        return;
      }

      try {
        const registration = await navigator.serviceWorker.ready;
        const sub = await registration.pushManager.getSubscription();
        setSubscription(sub);
      } catch (err) {
        console.error('Erro ao verificar subscription:', err);
      }
    };

    if (permission === 'granted') {
      checkSubscription();
    }
  }, [permission]);

  return {
    permission,
    subscription,
    loading,
    error,
    requestPermission,
    subscribeUser,
    unsubscribeUser,
    isSupported: 'serviceWorker' in navigator && 'PushManager' in window
  };
};